import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { useCity } from "../context/CityContext";
import { MapPin, CheckCircle2, Clock } from "lucide-react";
import WaitlistDialog from "./WaitlistDialog";

export default function CityPickerModal() {
  const { cities, city, pickerOpen, setPickerOpen, chooseCity } = useCity();
  const [waitlistCity, setWaitlistCity] = useState(null);
  const loc = useLocation();

  // Admins & auth pages shouldn't be greeted with a city picker.
  const hidden = ["/admin", "/login", "/register"];
  if (hidden.some((p) => loc.pathname.startsWith(p))) return null;

  const handlePick = (c) => {
    chooseCity(c);
    if (c.status !== "live") setWaitlistCity(c);
  };

  const preBookArea = (parentCity, area) => {
    setWaitlistCity({
      slug: parentCity.slug,
      name: `${area}, ${parentCity.name}`,
      launch: "Pre-booking · early access",
    });
  };

  return (
    <>
      <Dialog open={pickerOpen} onOpenChange={(o) => { if (!o) sessionStorage.setItem("hm_city_dismissed", "1"); setPickerOpen(o); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl flex items-center gap-2">
              <MapPin className="w-6 h-6" style={{ color: "var(--secondary)" }} />
              Choose your city
            </DialogTitle>
            <DialogDescription>
              Live in <b>Pune</b>, <b>Indore</b> and <b>Mumbai</b> (Thane, Kalyan, Bhiwandi). Other Mumbai areas are open for <b>pre-booking</b>. Rest of India from <b>01 Oct 2026</b>.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
            {cities.map((c) => {
              const live = c.status === "live";
              const selected = city?.slug === c.slug;
              return (
                <button
                  key={c.slug}
                  data-testid={`city-option-${c.slug}`}
                  onClick={() => handlePick(c)}
                  className={`text-left rounded-2xl border-2 p-4 transition-all card-lift ${
                    selected
                      ? "border-[color:var(--secondary)] bg-[color:var(--secondary)]/10"
                      : "border-black/10 bg-white hover:border-[color:var(--primary)]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-display text-lg" style={{ color: "var(--primary)" }}>{c.name}</div>
                    {live ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-600">
                        <CheckCircle2 className="w-3 h-3" /> Live
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-neutral-500">
                        <Clock className="w-3 h-3" /> Soon
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">{c.launch}</div>
                  {live && c.covered_areas?.length > 0 && (
                    <div className="mt-2 text-[10px] text-neutral-500 leading-snug line-clamp-2">
                      <b>Live in:</b> {c.covered_areas.slice(0, 4).join(", ")}
                      {c.covered_areas.length > 4 && "…"}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Mumbai pre-booking area chips */}
          {cities.find((c) => c.slug === "mumbai")?.prebook_areas?.length > 0 && (
            <div data-testid="mumbai-prebook-strip" className="mt-6 rounded-2xl border-2 border-amber-200 bg-amber-50/70 p-5">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-amber-700" />
                <div className="text-xs uppercase tracking-widest font-bold text-amber-800">
                  Mumbai — pre-booking open in these areas
                </div>
              </div>
              <p className="text-xs text-amber-900/70 mb-3">Tap your area to reserve early-access at HeylpMate launch pricing.</p>
              <div className="flex flex-wrap gap-2">
                {cities.find((c) => c.slug === "mumbai").prebook_areas.map((area) => (
                  <button
                    key={area}
                    data-testid={`prebook-mumbai-${area.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => preBookArea(cities.find((c) => c.slug === "mumbai"), area)}
                    className="px-3 py-1.5 rounded-full bg-white border border-amber-300 text-xs font-bold text-amber-900 hover:bg-amber-100 transition-colors"
                  >
                    + {area}
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <WaitlistDialog
        city={waitlistCity}
        open={!!waitlistCity}
        onClose={() => setWaitlistCity(null)}
      />
    </>
  );
}
